import { useState } from "react";
import { motion } from "framer-motion";
import {
    RiArrowLeftLine,
    RiGithubFill,
    RiExternalLinkLine,
} from "react-icons/ri";
import SectionTitle from "../../Common/SectionTitle";
import getImageLink from "../../../functions/getImageLink";

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { duration: 0.35, staggerChildren: 0.08 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

const PortfolioDetail = ({ content, closeDetail }) => {
    const {
        title,
        description,
        type,
        github,
        previewLink,
        technologies,
        keyFeatures,
        images,
    } = content;

    const [selectedImage, setSelectedImage] = useState(images[0]?.filename || null);

    const hasLinks = github || previewLink;

    return (
        <motion.div
            className="w-full text-white"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
        >
            {/* Back button + title */}
            <motion.div variants={itemVariants} className="mb-6">
                <button
                    onClick={closeDetail}
                    className="flex items-center gap-2 mb-5 transition-all duration-200"
                    style={{
                        fontSize: "12px",
                        fontWeight: 500,
                        padding: "6px 14px",
                        borderRadius: "8px",
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "rgba(255,255,255,0.6)",
                        cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(200,151,42,0.08)";
                        e.currentTarget.style.borderColor = "rgba(200,151,42,0.25)";
                        e.currentTarget.style.color = "#C8972A";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                        e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                        e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                    }}
                >
                    <RiArrowLeftLine size={14} />
                    <span>Back to Projects</span>
                </button>

                <SectionTitle SectionName={title} />

                {/* Type badge */}
                <span
                    className="inline-block mt-3"
                    style={{
                        fontSize: "10px",
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        padding: "3px 10px",
                        borderRadius: "4px",
                        background: "rgba(200,151,42,0.1)",
                        border: "1px solid rgba(200,151,42,0.2)",
                        color: "rgba(200,151,42,0.85)",
                    }}
                >
                    {type}
                </span>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Left / main column */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Image gallery */}
                    {images && images.length > 0 && (
                        <motion.div variants={itemVariants}>
                            {/* Main image */}
                            <div
                                className="w-full rounded-xl overflow-hidden"
                                style={{
                                    maxHeight: "320px",
                                    background: "rgba(255,255,255,0.03)",
                                    border: "1px solid rgba(255,255,255,0.07)",
                                }}
                            >
                                <motion.img
                                    key={selectedImage}
                                    src={getImageLink(selectedImage)}
                                    alt="Project preview"
                                    className="w-full h-full object-cover object-center"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.25 }}
                                    style={{ maxHeight: "320px" }}
                                />
                            </div>

                            {/* Thumbnails */}
                            {images.length > 1 && (
                                <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                                    {images.map((image) => {
                                        const isSelected = selectedImage === image.filename;
                                        return (
                                            <button
                                                key={image.id}
                                                onClick={() => setSelectedImage(image.filename)}
                                                className="flex-shrink-0 rounded-lg overflow-hidden transition-all duration-200"
                                                style={{
                                                    width: "72px",
                                                    height: "52px",
                                                    border: isSelected
                                                        ? "2px solid #C8972A"
                                                        : "2px solid rgba(255,255,255,0.08)",
                                                    opacity: isSelected ? 1 : 0.55,
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <img
                                                    src={getImageLink(image.filename)}
                                                    alt="Thumbnail"
                                                    className="w-full h-full object-cover"
                                                />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* Description */}
                    <motion.div
                        variants={itemVariants}
                        className="rounded-xl p-5"
                        style={{
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.07)",
                        }}
                    >
                        <p
                            className="font-semibold text-white mb-3"
                            style={{ fontSize: "13px", letterSpacing: "0.01em" }}
                        >
                            Overview
                        </p>
                        <p
                            className="leading-relaxed"
                            style={{ fontSize: "13px", color: "rgba(255,255,255,0.55)" }}
                        >
                            {description}
                        </p>
                    </motion.div>

                    {/* Key Features */}
                    {keyFeatures && keyFeatures.length > 0 && (
                        <motion.div
                            variants={itemVariants}
                            className="rounded-xl p-5"
                            style={{
                                background: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.07)",
                            }}
                        >
                            <p
                                className="font-semibold text-white mb-3"
                                style={{ fontSize: "13px" }}
                            >
                                Key Features
                            </p>
                            <ul className="space-y-2">
                                {keyFeatures.map((feature, index) => (
                                    <li
                                        key={index}
                                        className="flex items-start gap-2"
                                        style={{ fontSize: "12px", color: "rgba(255,255,255,0.5)" }}
                                    >
                                        <span
                                            className="flex-shrink-0 mt-[5px]"
                                            style={{
                                                width: "5px",
                                                height: "5px",
                                                borderRadius: "50%",
                                                background: "rgba(200,151,42,0.6)",
                                                display: "inline-block",
                                            }}
                                        />
                                        {feature.description}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    )}
                </div>

                {/* Right sidebar */}
                <div className="space-y-4">
                    {/* Technologies */}
                    {technologies && technologies.length > 0 && (
                        <motion.div
                            variants={itemVariants}
                            className="rounded-xl p-5"
                            style={{
                                background: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.07)",
                            }}
                        >
                            <p
                                className="font-semibold text-white mb-3"
                                style={{ fontSize: "13px" }}
                            >
                                Tech Stack
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {technologies.map((tech) => (
                                    <span
                                        key={tech.id}
                                        style={{
                                            fontSize: "10px",
                                            fontWeight: 500,
                                            padding: "3px 9px",
                                            borderRadius: "4px",
                                            background: "rgba(200,151,42,0.08)",
                                            border: "1px solid rgba(200,151,42,0.2)",
                                            color: "rgba(200,151,42,0.85)",
                                        }}
                                    >
                                        {tech.name}
                                    </span>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {/* Project Links */}
                    {hasLinks && (
                        <motion.div
                            variants={itemVariants}
                            className="rounded-xl p-5"
                            style={{
                                background: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.07)",
                            }}
                        >
                            <p
                                className="font-semibold text-white mb-3"
                                style={{ fontSize: "13px" }}
                            >
                                Project Links
                            </p>
                            <div className="space-y-2">
                                {github && (
                                    <a
                                        href={github}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 transition-all duration-200 group/link"
                                        style={{
                                            fontSize: "12px",
                                            color: "rgba(255,255,255,0.5)",
                                            padding: "8px 10px",
                                            borderRadius: "8px",
                                            border: "1px solid rgba(255,255,255,0.07)",
                                            background: "rgba(255,255,255,0.03)",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.color = "#C8972A";
                                            e.currentTarget.style.borderColor = "rgba(200,151,42,0.25)";
                                            e.currentTarget.style.background = "rgba(200,151,42,0.06)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.color = "rgba(255,255,255,0.5)";
                                            e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                                            e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                                        }}
                                    >
                                        <RiGithubFill size={15} />
                                        <span>Source Code</span>
                                    </a>
                                )}
                                {previewLink && (
                                    <a
                                        href={previewLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 transition-all duration-200"
                                        style={{
                                            fontSize: "12px",
                                            color: "rgba(255,255,255,0.5)",
                                            padding: "8px 10px",
                                            borderRadius: "8px",
                                            border: "1px solid rgba(255,255,255,0.07)",
                                            background: "rgba(255,255,255,0.03)",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.color = "#C8972A";
                                            e.currentTarget.style.borderColor = "rgba(200,151,42,0.25)";
                                            e.currentTarget.style.background = "rgba(200,151,42,0.06)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.color = "rgba(255,255,255,0.5)";
                                            e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                                            e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                                        }}
                                    >
                                        <RiExternalLinkLine size={15} />
                                        <span>Live Preview</span>
                                    </a>
                                )}
                            </div>
                        </motion.div>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default PortfolioDetail;
